import React from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import Button from '../../../../atoms/Button/Button';
import {
    isOfferedInMarket,
    PaymentMethod,
    paymentMethodGroups,
    PaymentMethodId,
    paymentMethodsForSegment,
    PriceCountry,
} from '../../../../../constants/merchantPriceCatalog';
import { SalesSegment } from '../../../../../types/onboarding/OnboardStore';
import {
    cellKey,
    PriceFormValues,
    ProductRows,
    productRowsOf,
    productRowsOfSegment,
    sortProductRows,
    withStandardProducts,
} from '../../../../../services/helpers/merchantPrices';
import AddProductCodeForm from './AddProductCodeForm';
import PaymentMethodEditRow from './PaymentMethodEditRow';
import PaymentMethodViewRow from './PaymentMethodViewRow';
import { Dash, GroupHeaderCell, PriceTable, Td, Th } from './styles';

interface CountryPriceTableProps {
    segment: SalesSegment;
    country: PriceCountry;
    /** The loaded values, for what the grid shows read-only. */
    defaults: PriceFormValues;
    canEdit: boolean;
    /** Whether only the methods active in this market and segment are shown. */
    onlyActive: boolean;
    /** Every country of the grid, since the standard codes fill covers all of them. */
    countries: PriceCountry[];
    /** Whether the merchant has no stored prices yet, which makes the standard codes fill the first step. */
    isEmptyList: boolean;
    /** Whether the list is being loaded, saved or synced, which holds the editing actions. */
    isBusy: boolean;
}

const COLUMNS = 6;

/** The prices of one country in one segment, a row per payment method, laid out like the country block of the appendix. */
const CountryPriceTable: React.FC<CountryPriceTableProps> = ({
    segment,
    country,
    defaults,
    canEdit,
    onlyActive,
    countries,
    isEmptyList,
    isBusy,
}: CountryPriceTableProps) => {
    const { getValues, setValue, reset } = useFormContext<PriceFormValues>();
    const activeValues = useWatch<PriceFormValues>({ name: 'active' }) as
        | Record<string, boolean>
        | undefined;
    const productValues = useWatch<PriceFormValues>({ name: 'products' }) as
        | ProductRows
        | undefined;

    const keyOf = (paymentMethod: PaymentMethodId) => cellKey(paymentMethod, country.code, segment);

    // The methods a code can be added to here: those the appendix offers in this market and segment.
    const addableMethods = paymentMethodsForSegment(segment).filter(
        method => method.id !== 'other' && isOfferedInMarket(method, country)
    );

    // The regular setup fills in the codes missing in every market and segment and switches those methods on; what is
    // there stays. Resetting against the kept defaults makes the fill count as an edit and lets every code row pick it up.
    const fillStandardCodes = () =>
        reset(withStandardProducts(getValues(), countries), {
            keepDefaultValues: true,
            keepErrors: true,
        });

    /** Writes the new code into the field array of its method row, kept in code order like the API returns them. */
    const addCode = (method: PaymentMethod, productCode: string, productType: string) => {
        const key = keyOf(method.id);
        const rows = getValues(`products.${key}`) ?? [];

        setValue(
            `products.${key}`,
            sortProductRows([...rows, { productCode: productCode.trim(), productType }]),
            { shouldDirty: true }
        );
    };

    const isActive = (paymentMethod: PaymentMethodId) =>
        (canEdit ? activeValues : defaults.active)?.[keyOf(paymentMethod)] === true;

    const hasCodes = (paymentMethod: PaymentMethodId) =>
        productRowsOf((canEdit ? productValues : defaults.products) ?? {}, keyOf(paymentMethod))
            .length > 0;

    const groups = paymentMethodGroups
        .map(group => ({
            group,
            methods: paymentMethodsForSegment(segment)
                .filter(method => method.group === group.id)
                // A method outside its markets, like the Other fallback, only shows when the merchant has a code there.
                .filter(
                    method =>
                        (method.id !== 'other' && isOfferedInMarket(method, country)) ||
                        hasCodes(method.id)
                )
                .filter(method => !onlyActive || isActive(method.id))
                .sort((a, b) => a.label.localeCompare(b.label)),
        }))
        .filter(({ methods }) => methods.length > 0);

    return (
        <section data-testid={`prices-${country.code}-${segment}`}>
            <PriceTable data-testid={`price-table-${country.code}-${segment}`}>
                <thead>
                    <tr>
                        <Th>Payment method</Th>
                        <Th>Active</Th>
                        <Th $numeric>Service fee (%)</Th>
                        <Th $numeric>Transaction fee ({country.currency})</Th>
                        <Th $numeric>Recurring service fee (%)</Th>
                        <Th $numeric>Recurring transaction fee ({country.currency})</Th>
                    </tr>
                </thead>
                <tbody>
                    {groups.length === 0 && (
                        <tr>
                            <Td colSpan={COLUMNS}>
                                <Dash>No active payment methods in this market and segment.</Dash>
                            </Td>
                        </tr>
                    )}
                    {groups.map(({ group, methods }) => (
                        <React.Fragment key={group.id}>
                            <tr data-testid={`price-group-${group.id}-${country.code}-${segment}`}>
                                <GroupHeaderCell colSpan={COLUMNS}>{group.label}</GroupHeaderCell>
                            </tr>
                            {methods.map(method =>
                                canEdit ? (
                                    <PaymentMethodEditRow
                                        key={method.id}
                                        method={method}
                                        country={country}
                                        segment={segment}
                                        defaults={defaults}
                                    />
                                ) : (
                                    <PaymentMethodViewRow
                                        key={method.id}
                                        method={method}
                                        country={country}
                                        segment={segment}
                                        values={defaults}
                                    />
                                )
                            )}
                        </React.Fragment>
                    ))}
                </tbody>
            </PriceTable>
            {canEdit && addableMethods.length > 0 && (
                <AddProductCodeForm
                    methods={addableMethods}
                    // A code can only be priced once per market and segment, whatever the method it is added to.
                    existingRows={() =>
                        productRowsOfSegment(getValues('products'), country.code, segment)
                    }
                    onAddCode={addCode}
                    aside={
                        <Button
                            type="button"
                            // The natural first step for a merchant without prices; a side option once there are some.
                            primary={isEmptyList}
                            disabled={isBusy}
                            data-testid="fill-standard-codes-button"
                            onClick={fillStandardCodes}
                        >
                            Fill with standard product codes
                        </Button>
                    }
                />
            )}
        </section>
    );
};

export default CountryPriceTable;
